import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/layout/Navigation';
import MobileDashboard from '../components/MobileDashboard';
import { Card, CardHeader, CardBody, CardFooter } from '../components/ui/Card';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import foodTrackingService, { DailyNutrition } from '../services/foodTrackingService';
import { isChronicEnabled } from '../config/appMode';

// Rute moștenite din modul CHRONIC care țin de modul "CHRONIC" (medical). În FII FIT
// (FITNESS) cardurile de pe Dashboard care duc la aceste rute sunt ascunse —
// este un filtru aplicat o singură dată la finalul array-ului `modules`,
// astfel încât să nu trebuiască să modificăm fiecare card în parte.
// NOTĂ: Rețeaua de specialiști RĂMÂNE activă în FII FIT — dar specializările
// din pagina /specialists sunt înlocuite cu cele de fitness (antrenori,
// nutriționiști sportivi, dieteticieni, kinetoterapeuți etc.), nu medici.
const CHRONIC_ONLY_PATHS = new Set([
  '/profile/medications',
  '/research',
  '/calendar',
  '/therapy-chat',
  '/emotional-wellbeing',
  '/care-circle',
  '/prescription-scanner',
  '/prescription-history',
]);


import {
  MessageCircle,
  UserCircle,
  ChefHat,
  Dumbbell,
  MapPin,
  CalendarClock,
  ShieldCheck,
  Brain,
  Star,
  PlayCircle,
  ArrowRight,
  Activity,
  Utensils,
  Pill,
  Flame,
  Target,
  Sparkles,
  Heart,
  BookOpen,
  Users,
  MessageCircleHeart,
  ThumbsUp,
  CheckCircle2,
  Camera,
  Trophy,
  Droplets,
} from 'lucide-react';
import axios from 'axios';
import { communityService, type Post } from '../services/communityService';

// ─── Desktop Macro Bar ─────────────────────────────────────────────────────
function MacroBar({ label, consumed, goal, color }: {
  label: string; consumed: number; goal: number; color: string;
}) {
  const pct = goal > 0 ? Math.min((consumed / goal) * 100, 100) : 0;
  return (
    <div className="flex-1">
      <p className="text-xs font-medium text-gray-600 mb-1">{label}</p>
      <div className="w-full bg-gray-100 rounded-full h-2">
        <div className={`${color} h-2 rounded-full transition-all duration-300`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-gray-400 mt-1">{Math.round(consumed)}/{goal}g</p>
    </div>
  );
}

interface DashboardStats {
  profileCompletion: number;
  activeMealPlans: number;
  chatConversations: number;
  trackingLogs: number;
  currentStreak: number;
  activeGoals: number;
  foodEntriesToday: number;
  aiPatterns: number;
}

interface RecentActivity {
  type: string;
  title: string;
  description: string;
  time: string;
  timestamp: Date;
  icon: any;
  iconBg: string;
  iconColor: string;
}

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState<DashboardStats>({
    profileCompletion: 0,
    activeMealPlans: 0,
    chatConversations: 0,
    trackingLogs: 0,
    currentStreak: 0,
    activeGoals: 0,
    foodEntriesToday: 0,
    aiPatterns: 0
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [communityPosts, setCommunityPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  // ─── Calorie data (desktop widget) ───────────────────────────────────────
  const [calorieData, setCalorieData] = useState<DailyNutrition | null>(null);
  const [calorieLoading, setCalorieLoading] = useState(true);

  const getLocalDateString = (d = new Date()) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  useEffect(() => {
    const fetchCalories = async () => {
      try {
        const today = getLocalDateString();
        const data = await foodTrackingService.getDailyNutrition(today);
        setCalorieData(data);
      } catch {
        setCalorieData({
          date: getLocalDateString(),
          entries: [],
          totals: { calories: 0, protein: 0, carbs: 0, fat: 0, quantity: 0 },
          remaining: { calories: 2000, protein: 150, carbs: 250, fat: 65 },
          byMealType: {
            breakfast: { calories: 0, protein: 0, carbs: 0, fat: 0 },
            lunch: { calories: 0, protein: 0, carbs: 0, fat: 0 },
            dinner: { calories: 0, protein: 0, carbs: 0, fat: 0 },
            snack: { calories: 0, protein: 0, carbs: 0, fat: 0 },
          },
        });
      } finally {
        setCalorieLoading(false);
      }
    };
    fetchCalories();
  }, []);

  const calorieGoal = calorieData?.goals?.targetCalories || 2000;
  const calorieConsumed = calorieData?.totals?.calories || 0;
  const calorieRemaining = calorieData?.remaining?.calories || calorieGoal;
  const caloriePercentage = calorieGoal > 0 ? Math.min((calorieConsumed / calorieGoal) * 100, 100) : 0;
  const isOverGoal = calorieConsumed > calorieGoal;

  const proteinConsumed = calorieData?.totals?.protein || 0;
  const proteinGoal = calorieData?.goals?.targetProtein || 150;
  const carbsConsumed = calorieData?.totals?.carbs || 0;
  const carbsGoal = calorieData?.goals?.targetCarbs || 250;
  const fatConsumed = calorieData?.totals?.fat || 0;
  const fatGoal = calorieData?.goals?.targetFats || 65;

  const formatDateRo = () =>
    new Intl.DateTimeFormat('ro-RO', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date());

  // Redirect specialist to their dashboard
  useEffect(() => {
    if (user?.userType === 'SPECIALIST') {
      navigate('/specialist/dashboard');
    }
  }, [user, navigate]);

  // Redirect if lifestyle mode not set (first-time users)
  useEffect(() => {
    // Only check after auth is loaded
    if (!authLoading && user && user.userType === 'PATIENT' && !user.lifestyleMode) {
      navigate('/lifestyle-mode');
    }
  }, [authLoading, user, navigate]);


  // Restore scroll position when returning to dashboard
  useEffect(() => {
    const savedScroll = sessionStorage.getItem('dashboardScrollY');
    if (savedScroll) {
      const scrollY = parseInt(savedScroll, 10);
      // Use requestAnimationFrame to ensure DOM is ready before scrolling
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      });
      sessionStorage.removeItem('dashboardScrollY');
    }
  }, [location.key]);

  // Save scroll position when navigating away from dashboard
  useEffect(() => {
    const handleBeforeNavigate = () => {
      sessionStorage.setItem('dashboardScrollY', String(window.scrollY));
    };
    window.addEventListener('beforeunload', handleBeforeNavigate);
    return () => {
      // Save scroll position when component unmounts (navigating away)
      sessionStorage.setItem('dashboardScrollY', String(window.scrollY));
      window.removeEventListener('beforeunload', handleBeforeNavigate);
    };
  }, []);

  // Fetch real dashboard data - refetch EVERY TIME user navigates to dashboard
  // location.key changes on every navigation, even to the same route
  useEffect(() => {
    if (user) {
      console.log('[DASHBOARD] 🔄 Navigation detected - refreshing all data...');
      fetchDashboardData();
    }
  }, [location.key, user?.id]); // Refetch on EVERY navigation to dashboard

  const fetchDashboardData = async () => {
    // Optimizare: încarcă datele progresiv, nu toate odată
    setLoading(false); // Arată UI-ul imediat cu date default
    
    try {
      const token = localStorage.getItem('accessToken');
      
      // Fetch profile - DISABLE CACHE to always get fresh data
      let profileCompletion = 0;
      try {
        const profileRes = await axios.get(
          `${import.meta.env.VITE_API_URL}/api/profile?_t=${Date.now()}`,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            } 
          }
        );
        if (profileRes.data) {
          // Use completion percentage calculated by backend
          // Backend automatically calculates based on lifestyleMode (HEALTHY vs CHRONIC)
          profileCompletion = profileRes.data.completionPercent || 0;
          console.log('[DASHBOARD] 📊 Profile completion from backend:', profileCompletion + '%');
        }
      } catch (err) {
        console.log('No profile yet');
      }

      // Fetch meal plans
      let activeMealPlans = 0;
      try {
        const plansRes = await axios.get(
          `${import.meta.env.VITE_API_URL}/api/nutrition/meal-plans`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        activeMealPlans = plansRes.data?.length || 0;
      } catch (err) {
        console.log('No meal plans');
      }

      // Fetch chat conversations
      let chatConversations = 0;
      try {
        const chatRes = await axios.get(
          `${import.meta.env.VITE_API_URL}/api/chat/conversations`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        chatConversations = chatRes.data?.length || 0;
      } catch (err) {
        console.log('No chat conversations');
      }

      // Fetch tracking stats for count
      let trackingLogs = 0;
      try {
        const statsRes = await axios.get(
          `${import.meta.env.VITE_API_URL}/api/tracking/stats?period=month`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Count from dailyStats (sum all medications + meals + exercises)
        if (statsRes.data?.dailyStats) {
          trackingLogs = statsRes.data.dailyStats.reduce((sum: number, day: any) => {
            return sum + (day.medications || 0) + (day.meals || 0) + (day.exercises || 0);
          }, 0);
        }
      } catch (err) {
        console.log('No tracking logs');
      }

      // Fetch streak
      let currentStreak = 0;
      try {
        const streakRes = await axios.get(
          `${import.meta.env.VITE_API_URL}/api/therapy/check-in/streak`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        currentStreak = streakRes.data?.currentStreak || 0;
      } catch (err) {
        console.log('No streak');
      }

      // Fetch active goals
      let activeGoals = 0;
      try {
        const goalsRes = await axios.get(
          `${import.meta.env.VITE_API_URL}/api/goals?isActive=true`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        activeGoals = goalsRes.data?.goals?.length || 0;
      } catch (err) {
        console.log('No goals');
      }

      // Fetch food entries today
      let foodEntriesToday = 0;
      try {
        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
        const foodRes = await axios.get(
          `${import.meta.env.VITE_API_URL}/api/food-tracking/entries?date=${today}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        foodEntriesToday = foodRes.data?.entries?.length || 0;
      } catch (err) {
        console.log('No food entries');
      }

      // Fetch AI patterns
      let aiPatterns = 0;
      try {
        const analyticsRes = await axios.get(
          `${import.meta.env.VITE_API_URL}/api/tracking/analytics/summary`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        aiPatterns = analyticsRes.data?.patterns?.length || 0;
      } catch (err) {
        console.log('No AI patterns');
      }

      setStats({
        profileCompletion,
        activeMealPlans,
        chatConversations,
        trackingLogs,
        currentStreak,
        activeGoals,
        foodEntriesToday,
        aiPatterns
      });

      // Fetch recent community posts
      try {
        const postsData = await communityService.getPosts({
          sortBy: 'recent',
          limit: 3
        });
        setCommunityPosts(postsData.posts || []);
      } catch (err) {
        console.log('No community posts');
      }

      // Build recent activity from multiple tracking endpoints
      try {
        const activities: RecentActivity[] = [];
        
        // Fetch recent meals
        try {
          const mealsRes = await axios.get(
            `${import.meta.env.VITE_API_URL}/api/tracking/meals?limit=5`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (mealsRes.data?.logs) {
            mealsRes.data.logs.forEach((log: any) => {
              const date = new Date(log.consumedAt); // Use consumedAt instead of timestamp
              if (!isNaN(date.getTime())) {
                // Extract food names from foods JSON array
                const foodNames = Array.isArray(log.foods) && log.foods.length > 0
                  ? log.foods.map((f: any) => f.name || 'Aliment').join(', ')
                  : log.mealType || 'Masă';
                
                activities.push({
                  type: 'meal',
                  title: 'Masă înregistrată',
                  description: foodNames,
                  time: getTimeAgo(date),
                  timestamp: date,
                  icon: Utensils,
                  iconBg: 'bg-green-100',
                  iconColor: 'text-green-600'
                });
              }
            });
          }
        } catch (err) {
          console.log('No meal logs');
        }
        
        // Fetch recent medications
        try {
          const medsRes = await axios.get(
            `${import.meta.env.VITE_API_URL}/api/tracking/medications?limit=5`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (medsRes.data?.logs) {
            medsRes.data.logs.forEach((log: any) => {
              const date = new Date(log.takenAt); // Use takenAt instead of timestamp
              if (!isNaN(date.getTime())) {
                // Get medication name from nested object
                const medName = log.medication?.name || 'Medicament';
                
                activities.push({
                  type: 'medication',
                  title: 'Medicație luată',
                  description: medName,
                  time: getTimeAgo(date),
                  timestamp: date,
                  icon: Pill,
                  iconBg: 'bg-blue-100',
                  iconColor: 'text-blue-600'
                });
              }
            });
          }
        } catch (err) {
          console.log('No medication logs');
        }
        
        // Fetch recent exercises
        try {
          const exercisesRes = await axios.get(
            `${import.meta.env.VITE_API_URL}/api/tracking/exercises?limit=5`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (exercisesRes.data?.logs) {
            exercisesRes.data.logs.forEach((log: any) => {
              const date = new Date(log.performedAt); // Use performedAt instead of timestamp
              if (!isNaN(date.getTime())) {
                // Use exerciseName from backend
                const exerciseName = log.exerciseName || 'Exercițiu';
                
                activities.push({
                  type: 'exercise',
                  title: 'Exercițiu completat',
                  description: `${exerciseName} - ${log.duration || '0'} minute`,
                  time: getTimeAgo(date),
                  timestamp: date,
                  icon: Activity,
                  iconBg: 'bg-purple-100',
                  iconColor: 'text-purple-600'
                });
              }
            });
          }
        } catch (err) {
          console.log('No exercise logs');
        }
        
        // Sort by most recent (newest first)
        activities.sort((a, b) => {
          return b.timestamp.getTime() - a.timestamp.getTime();
        });
        
        // Take top 6
        setRecentActivity(activities.slice(0, 6));
      } catch (err) {
        console.log('No recent activity');
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `Acum ${diffMins} ${diffMins === 1 ? 'minut' : 'minute'}`;
    if (diffHours < 24) return `Acum ${diffHours} ${diffHours === 1 ? 'oră' : 'ore'}`;
    if (diffDays === 1) return 'Ieri';
    return `Acum ${diffDays} zile`;
  };


  const modules = [
    {
      icon: UserCircle,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      title: 'Profilul Meu',
      subtitle: `${stats.profileCompletion}% completat`,
      description: 'Gestionează obiectivele fitness, date biometrice, preferințe alimentare. Date securizate GDPR.',
      footer: 'Date criptate',
      footerIcon: ShieldCheck,
      footerColor: 'text-blue-600',
      path: '/profile',
      // Badge config
      badgeValue: `${stats.profileCompletion}%`,
      badgeColor: stats.profileCompletion === 100 ? 'bg-green-500' : stats.profileCompletion >= 50 ? 'bg-orange-500' : 'bg-gray-400',
      showBadge: true
    },
    {
      icon: ChefHat,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      title: 'Nutriție',
      subtitle: `${stats.activeMealPlans} ${stats.activeMealPlans === 1 ? 'plan activ' : 'planuri active'}`,
      description: 'Planuri de mese AI personalizate 7 zile, rețete video și liste de cumpărături.',
      footer: 'AI Personalizat',
      footerIcon: Brain,
      footerColor: 'text-green-600',
      path: '/nutrition',
      // Badge config
      badgeValue: stats.activeMealPlans.toString(),
      badgeColor: stats.activeMealPlans > 0 ? 'bg-green-500' : 'bg-gray-400',
      showBadge: true
    },
    {
      icon: Dumbbell,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      title: 'Exerciții',
      subtitle: 'Video adaptate',
      description: 'Exerciții pe 3 nivele: ușor/mediu/avansat. Filtre pentru tipul de exercițiu necesar.',
      footer: '100+ video-uri',
      footerIcon: PlayCircle,
      footerColor: 'text-purple-600',
      path: '/exercises',
      showBadge: false
    },
    {
      icon: MessageCircle,
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      title: 'Chat AI Fitness',
      subtitle: `${stats.chatConversations} ${stats.chatConversations === 1 ? 'conversație' : 'conversații'}`,
      description: 'Asistent AI specializat pentru întrebări despre nutriție, antrenamente și fitness 24/7.',
      footer: 'Asistent inteligent',
      footerIcon: Brain,
      footerColor: 'text-indigo-600',
      path: '/chat',
      // Badge config
      badgeValue: stats.chatConversations.toString(),
      badgeColor: stats.chatConversations > 0 ? 'bg-indigo-500' : 'bg-gray-400',
      showBadge: true
    },
    {
      icon: MapPin,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      title: 'Specialiști Fitness',
      subtitle: 'Hartă interactivă',
      description: 'Găsește antrenori personali, nutriționiști sportivi, dieteticieni, kinetoterapeuți și instructori de yoga/pilates în zona ta.',
      footer: 'Review-uri reale',

      footerIcon: Star,
      footerColor: 'text-red-600',
      path: '/specialists',
      showBadge: false
    },
    {
      icon: CalendarClock,
      iconBg: 'bg-pink-100',
      iconColor: 'text-pink-600',
      title: 'Calendar & Tracking',
      subtitle: `${stats.trackingLogs} ${stats.trackingLogs === 1 ? 'log' : 'log-uri'}`,
      description: 'Urmărește mese, medicamente și exerciții. Calendar interactiv și statistici.',
      footer: 'Tracking complet',
      footerIcon: Activity,
      footerColor: 'text-pink-600',
      path: '/calendar',
      // Badge config
      badgeValue: stats.trackingLogs.toString(),
      badgeColor: stats.trackingLogs > 0 ? 'bg-pink-500' : 'bg-gray-400',
      showBadge: true
    },
    {
      icon: Utensils,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      title: 'Mese & Calorii cu AI',
      subtitle: `${stats.foodEntriesToday} ${stats.foodEntriesToday === 1 ? 'aliment azi' : 'alimente azi'}`,
      description: 'Fotografiază mâncarea sau adaugă mesele manual, iar AI îți recunoaște mâncarea și îți estimează caloriile și valorile nutriționale.',
      footer: 'AI Vision',
      footerIcon: Brain,
      footerColor: 'text-amber-600',
      path: '/food-tracking',
      // Badge config
      badgeValue: stats.foodEntriesToday.toString(),
      badgeColor: stats.foodEntriesToday > 0 ? 'bg-amber-500' : 'bg-gray-400',
      showBadge: true
    },
    {
      icon: Heart,
      iconBg: 'bg-rose-100',
      iconColor: 'text-rose-600',
      title: 'Jurnal Emoțional',
      subtitle: `Streak ${stats.currentStreak} ${stats.currentStreak === 1 ? 'zi' : 'zile'}`,
      description: 'Check-in zilnic pentru dispoziție, emoții și intensitate. Urmărește trendul săptămânal.',
      footer: 'Daily check-in',
      footerIcon: Flame,
      footerColor: 'text-rose-600',
      path: '/emotional-wellbeing',
      // Badge config
      badgeValue: stats.currentStreak.toString(),
      badgeColor: stats.currentStreak > 0 ? 'bg-rose-500' : 'bg-gray-400',
      showBadge: true
    },
    {
      icon: Sparkles,
      iconBg: 'bg-cyan-100',
      iconColor: 'text-cyan-600',
      title: 'AI Tracking',
      subtitle: `${stats.aiPatterns} ${stats.aiPatterns === 1 ? 'pattern' : 'pattern-uri'}`,
      description: 'Insights inteligente: pattern-uri, corelații și predicții bazate pe datele tale.',
      footer: 'AI Fitness',
      footerIcon: Brain,
      footerColor: 'text-cyan-600',
      path: '/insights',
      // Badge config
      badgeValue: stats.aiPatterns.toString(),
      badgeColor: stats.aiPatterns > 0 ? 'bg-cyan-500' : 'bg-gray-400',
      showBadge: true
    },
    {
      icon: BookOpen,
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      title: 'Educație & Resurse',
      subtitle: 'Articole & FAQs',
      description: 'Articole, ghiduri video și răspunsuri la întrebările tale despre fitness și nutriție.',
      footer: 'Conținut fitness',
      footerIcon: Brain,
      footerColor: 'text-teal-600',
      path: '/education',
      showBadge: false
    },
    {
      icon: Camera,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      title: 'Fotografii Progres',
      subtitle: 'Înainte & După',
      description: 'Fă poze de progres, urmărește transformarea în timp și compară fotografii din zile diferite față în față.',
      footer: 'Comparație vizuală',
      footerIcon: Camera,
      footerColor: 'text-orange-600',
      path: '/progress-photos',
      showBadge: false
    },
    {
      icon: BookOpen,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      title: 'Jurnal',
      subtitle: 'Tranzit & Notițe',
      description: 'Urmărește tranzitul intestinal zilnic (Scala Bristol) și adaugă notițe personale despre cum ți-a fost ziua.',
      footer: 'Wellness zilnic',
      footerIcon: Activity,
      footerColor: 'text-amber-600',
      path: '/journal',
      showBadge: false
    },
    {
      icon: Users,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      title: 'Cerc de Îngrijire',
      subtitle: 'Familie & Medic',
      description: 'Permite familiei sau medicului tău să monitorizeze indicatorii tăi de sănătate în timp real, cu acordul tău.',
      footer: 'Acces securizat',
      footerIcon: ShieldCheck,
      footerColor: 'text-blue-600',
      path: '/care-circle',
      showBadge: false
    },
    {
      icon: Trophy,
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      title: 'Recompense',
      subtitle: 'Obiective & Invitatii',
      description: 'Castiga credite pentru activitate, contributii in comunitate si invitatii de prieteni. Urmareste progresul si deblocheaza recompense.',
      footer: 'Credite bonus',
      footerIcon: Trophy,
      footerColor: 'text-yellow-600',
      path: '/rewards',
      showBadge: false
    },
    {
      icon: Flame,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      title: 'Retete Comunitate',
      subtitle: 'Food Community',
      description: 'Posteaza retete cu poze, descopera ce gatesc ceilalti, salveaza retete favorite si obtine analiza nutritionala automata cu AI.',
      footer: 'AI Nutritie',
      footerIcon: ChefHat,
      footerColor: 'text-orange-600',
      path: '/food-community',
      showBadge: false
    },
    {
      icon: Droplets,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-500',
      title: 'Aport Apă',
      subtitle: 'Hidratare zilnică',
      description: 'Urmărește câtă apă bei zilnic, setează obiective de hidratare și primește remindere pentru a bea apă regulat.',
      footer: 'Tracking hidratare',
      footerIcon: Droplets,
      footerColor: 'text-blue-500',
      path: '/water-intake',
      showBadge: false
    },
    {
      icon: MessageCircle,
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
      title: 'Întreabă AI-ul Fitness',
      subtitle: 'Asistent 24/7',
      description: 'Asistent 24/7 pentru fitness-ul tău. Întreabă orice despre nutriție, antrenamente, recuperare sau obiective fitness.',
      footer: 'Răspunsuri instant',
      footerIcon: Brain,
      footerColor: 'text-violet-600',
      path: '/chat',
      showBadge: false
    }
  ].filter((m) => isChronicEnabled() || !CHRONIC_ONLY_PATHS.has(m.path));

  // Personalized content based on healthGoal
  const getPersonalizedContent = () => {
    const healthGoal = user?.healthGoal;

    // Debug logging
    console.log('🎯 [DASHBOARD] Current healthGoal:', healthGoal);
    console.log('👤 [DASHBOARD] User object:', user);

    // Default tips if no goal set
    if (!healthGoal) {
      console.warn('⚠️  [DASHBOARD] No healthGoal set for user');
      return {
        tips: [
          '💡 Setează un obiectiv de sănătate pentru recomandări personalizate',
          '📊 Completează profilul pentru un plan mai bun',
          '🥗 Începe să tracking alimentele pentru rezultate mai bune'
        ],
        focusAreas: ['Completează profilul', 'Setează obiectiv', 'Începe tracking']
      };
    }

    // HEALTHY MODE GOALS
    const healthyGoalsTips: Record<string, any> = {
      WEIGHT_LOSS: {
        tips: [
          '🔥 Deficit caloric: Consumă cu 300-500 kcal mai puțin decât arzi',
          '💧 Hidratare: Bea 2-3L apă zilnic pentru metabolism optim',
          '🏃 Cardio HIIT: 20-30 min, 3-4x/săptămână pentru ardere maximă'
        ],
        focusAreas: ['Tracking calorii', 'Cardio regulat', 'Somn 7-8h']
      },
      MUSCLE_GAIN: {
        tips: [
          '💪 Surplus caloric: +300-500 kcal/zi cu focus pe proteine',
          '🥩 Proteine: 1.6-2g/kg corp - carne, ouă, lactate, leguminoase',
          '🏋️ Antrenament forță: 4-5x/săptămână, progresie constantă'
        ],
        focusAreas: ['Tracking proteine', 'Antrenament forță', 'Recuperare']
      },
      HEALTHY_LIFESTYLE: {
        tips: [
          '🥗 Nutriție echilibrată: 50% carbohidrați, 30% proteine, 20% grăsimi',
          '🚶 Mișcare zilnică: Minim 8000 pași sau 30 min activitate',
          '😴 Somn calitativ: 7-9 ore, aceeași oră culcare/trezire'
        ],
        focusAreas: ['Nutriție echilibrată', 'Activitate zilnică', 'Rutină somn']
      },
      REDUCE_BELLY_FAT: {
        tips: [
          '🔥 HIIT Training: Intervaluri intense 20-30 min, 3-4x/săptămână',
          '🚫 Reduce zahar & carbohidrați simpli: Focus pe proteine & fibre',
          '⏰ Post intermitent (opțional): 16:8 sau 14:10 pentru cetoza'
        ],
        focusAreas: ['HIIT exercises', 'Măsurători talie', 'Dietă low-carb']
      },
      INCREASE_ENERGY: {
        tips: [
          '🥑 Dietă antiinflamatoare: Omega-3, fructe, legume, nuci',
          '⚡ Evită "food crash": Mănâncă la 3-4 ore, evită zahăr rafinat',
          '😴 Somn profund: 7-9 ore calitative, evită ecrane 1h înainte'
        ],
        focusAreas: ['Alimente energizante', 'Somn regulat', 'Hidratare']
      },
      CLEAN_EATING: {
        tips: [
          '🌿 Alimente neprocesate: Fructe, legume, carne proaspătă, cereale integrale',
          '📋 Citește etichetele: Max 5 ingrediente, fără E-uri sau aditivi',
          '🏡 Gătește acasă: Control total asupra ingredientelor'
        ],
        focusAreas: ['Alimente curate', 'Rețete simple', 'Fără procesate']
      },
      REDUCE_SUGAR_CARBS: {
        tips: [
          '🥒 Low-carb: <100g carbohidrați/zi, focus pe legume verzi',
          '🍞 Înlocuiește: Pâine → pâine integrală, paste → zoodles',
          '🚨 Zahăr ascuns: Atenție la sosuri, sucuri, iaurt aromat'
        ],
        focusAreas: ['Rețete low-carb', 'Tracking carbohidrați', 'Alerte zahăr']
      },
      BETTER_SLEEP: {
        tips: [
          '😴 Rutină seară: Aceeași oră culcare, ritual relaxare 30 min',
          '🥛 Alimente pentru somn: Migdale, banane, ceai de mușețel',
          '📱 Detox digital: Fără ecrane 1h înainte, mod noapte telefon'
        ],
        focusAreas: ['Rutină somn', 'Tracking somn', 'Respirație ghidată']
      },
      REDUCE_BLOATING: {
        tips: [
          '🥗 Dietă FODMAP: Elimină alimentele care fermentează: usturoi, ceapă, fasole',
          '📝 Jurnal alimentație: Notează ce mănânci + reacția corpului',
          '💧 Hidratare + probiotice: Apă + iaurt natural pentru digestie'
        ],
        focusAreas: ['Plan FODMAP', 'Jurnal simptome', 'Alimente blânde']
      },
      START_EXERCISE: {
        tips: [
          '🚶 Nivel începător: Start cu 10-15 min/zi, creștere graduală',
          '📹 Video ghidate: Urmează exerciții pas cu pas, fără echipament',
          '🎯 Pași zilnici: Obiectiv 5000 → 8000 → 10,000 progresiv'
        ],
        focusAreas: ['Video începători', 'Tracking pași', 'Motivație AI']
      },
      IMPROVE_FOCUS: {
        tips: [
          '🧠 Dietă pentru creier: Omega-3 (pește), magneziu, afine, nuci',
          '🧘 Exerciții respirație: 5-10 min meditație, respirație profundă',
          '⏸️ Pauze ghidate: Tehnica Pomodoro - 25 min lucru, 5 min pauză'
        ],
        focusAreas: ['Alimente creier', 'Meditație', 'Tehnici focus']
      },
      MAINTAIN_WEIGHT: {
        tips: [
          '⚖️ Echilibru caloric: Consumă cât arzi pentru menținere',
          '🏃 Activitate regulată: 3-4x/săptămână mixt cardio + forță',
          '📊 Monitoring lunar: Cântărește-te săptămânal, ajustează dacă e nevoie'
        ],
        focusAreas: ['Tracking calorii', 'Antrenament regulat', 'Greutate stabilă']
      }
    };

    // CHRONIC MODE GOALS
    const chronicGoalsTips: Record<string, any> = {
      EASY_STANDING: {
        tips: [
          '💪 Exerciții picioare: Squats pe scaun, ridicări gambe, step-ups ușoare',
          '🔄 Zilnic 10-15 min: Constanță e cheia, progres treptat',
          '📈 Monitorizare progres: Notează câte repetări faci, creștere săptămânală'
        ],
        focusAreas: ['Lower body exercises', 'Tracking progres', 'Forță picioare']
      },
      REDUCE_INFLAMMATION: {
        tips: [
          '🥗 Plan antiinflamator: Omega-3, curcuma, ghimbir, legume verzi',
          '🚫 Elimină pro-inflamatorii: Zahăr, carne procesată, ulei rafinat',
          '💊 Suplimente: Omega-3, vitamina D, curcuma (după consultare)'
        ],
        focusAreas: ['Dietă antiinflamatoare', 'Suplimente', 'Tracking CRP']
      },
      RECOVER_MUSCLE_STRENGTH: {
        tips: [
          '🏋️ Greutate proprie: Push-ups pe genunchi, plank, lunges',
          '📹 Kinetoterapie video: Exerciții ghidate pentru recuperare',
          '📅 Evaluare lunară: Testează forța, crește dificultatea treptat'
        ],
        focusAreas: ['Exerciții recuperare', 'Video terapie', 'Progres lunar']
      },
      CLIMB_STAIRS: {
        tips: [
          '🫁 Respirație controlată: Inspiră pe nas 4s, expiră 6s',
          '🦵 Mobilitate articulară: Stretching genunchi, șolduri zilnic',
          '🎯 Antrenament practic: Urcă 2-3 trepte → pauză → repeat'
        ],
        focusAreas: ['Exerciții respirație', 'Mobilitate', 'Cardio ușor']
      },
      REDUCE_JOINT_PAIN: {
        tips: [
          '🥗 Nutriție antiinflamatorie: Pește gras, fructe roșii, nuci',
          '🧘 Exerciții blânde: Yoga, Tai Chi, stretching pentru articulații',
          '🧊 Comprese: Gheață pentru inflamație, căldură pentru rigiditate'
        ],
        focusAreas: ['Dietă articulații', 'Exerciții blânde', 'Pain log']
      },
      BREATHE_BETTER: {
        tips: [
          '🫁 Exerciții respirație: Respirație diafragmală, pursed-lip breathing',
          '🛏️ Poziții somn: Cap ridicat 30-45°, poziție laterală',
          '🥗 Plan anti-mucus: Evită lactate, zahăr; consumă ghimbir, ceai'
        ],
        focusAreas: ['Respirație ghidată', 'Poziții somn', 'Dietă pulmonară']
      },
      STABILIZE_GLUCOSE: {
        tips: [
          '🩸 Tracking glicemie: Măsoară dimineața și post-prandial',
          '🥗 Alimentație diabetici: Low GI, fibre, proteine, grăsimi sănătoase',
          '📊 Grafic evoluție: Monitorizează trending, ajustează alimentația'
        ],
        focusAreas: ['Tracking glicemie', 'Rețete diabetici', 'Grafice']
      },
      IMPROVE_BALANCE: {
        tips: [
          '🧘 Exerciții echilibru: Stai pe un picior 30s, heel-to-toe walk',
          '🤸 Coordonare: Tai Chi, yoga pentru stabilitate',
          '🛡️ Prevenție căderi: Elimină covoare, iluminat bun, bare sprijin'
        ],
        focusAreas: ['Exerciții echilibru', 'Video Tai Chi', 'Safety tips']
      },
      REDUCE_CHRONIC_FATIGUE: {
        tips: [
          '😴 Somn profund: 8-9 ore, aceeași oră, cameră întunecată',
          '🥗 Nutriție anti-oboseală: Fier, B12, magneziu, hidratare',
          '🧘 Relaxare: Meditație 10 min, respirație, yoga ușoară'
        ],
        focusAreas: ['Somn calitativ', 'Nutriție energie', 'Tehnici relaxare']
      },
      IMPROVE_DIGESTION: {
        tips: [
          '🥗 Fără iritanți: Elimină picant, gras, acid, alcool',
          '📋 Plan FODMAP: Testează alimente, găsește trigger-ele',
          '📝 Jurnal alimentație + simptome: Corelează ce mănânci cu cum te simți'
        ],
        focusAreas: ['Dietă FODMAP', 'Jurnal simptome', 'Alimente blânde']
      },
      REGAIN_MOBILITY: {
        tips: [
          '🤲 Exerciții zilnice: ROM exercises pentru mâini/picioare 10-15 min',
          '📹 Video adaptate: Exerciții ghidate pe niveluri dificultate',
          '📈 Tracking progres: Notează flexibilitate, range of motion'
        ],
        focusAreas: ['ROM exercises', 'Video terapie', 'Progres săptămânal']
      },
      PREVENT_DISEASE_WORSENING: {
        tips: [
          '🥗 Alimentație: Plan nutrițional specific bolii tale',
          '💧 Hidratare: 2-3L apă zilnic, esențial pentru funcții vitale',
          '💊 Medicație: Respectă prescripția, tracking administrare'
        ],
        focusAreas: ['Nutriție boală', 'Hidratare', 'Compliance medicație']
      },
      HEALTHY_WEIGHT_LOSS_CHRONIC: {
        tips: [
          '🐌 Ritm sănătos: 0.25-0.5 kg/săptămână, fără foame extremă',
          '🥗 Dietă adaptată: Ușor hipocaloric, rețete potrivite bolii',
          '💬 Motivație + suport: Check-in-uri AI, comunitate, progres vizual'
        ],
        focusAreas: ['Dietă ușoară', 'Rețete adaptate', 'Tracking greutate']
      },
      CONTROL_CHRONIC_PAIN: {
        tips: [
          '📝 Jurnal durere: Notează intensitate 1-10, locație, trigger-e',
          '🧘 Activități reducere: Yoga, meditație, breathing exercises',
          '💬 Suport psihologic: Chat AI terapeutic, comunitate, resurse'
        ],
        focusAreas: ['Pain tracking', 'Tehnici management', 'Suport emoțional']
      },
      IMPROVE_LIFE_WITH_DISEASE: {
        tips: [
          '🎯 Personalizare AI: Recomandări bazate pe boală + simptome',
          '📚 Educație: articole și video-uri pentru obiectivele tale',
          '📊 Tracking simptome: Monitorizare completă, insights AI'
        ],
        focusAreas: ['AI personalizat', 'Educație', 'Tracking complet']
      }
    };

    const allGoals = { ...healthyGoalsTips, ...chronicGoalsTips };
    const content = allGoals[healthGoal];
    
    if (!content) {
      console.error(`❌ [DASHBOARD] Unknown healthGoal: "${healthGoal}"`);
      console.error(`❌ [DASHBOARD] Available goals:`, Object.keys(allGoals));
      console.error(`❌ [DASHBOARD] Using fallback: HEALTHY_LIFESTYLE`);
      
      // Return fallback with warning message
      return {
        tips: [
          `⚠️ Obiectivul "${healthGoal}" nu este recunoscut - folosim setări default`,
          '🥗 Nutriție echilibrată: 50% carbohidrați, 30% proteine, 20% grăsimi',
          '🚶 Mișcare zilnică: Minim 8000 pași sau 30 min activitate'
        ],
        focusAreas: ['Schimbă obiectivul', 'Nutriție echilibrată', 'Activitate zilnică']
      };
    }
    
    console.log(`✅ [DASHBOARD] Successfully loaded tips for: ${healthGoal}`);
    return content;
  };

  // Memoize personalized content to prevent infinite re-renders
  const personalizedContent = useMemo(() => getPersonalizedContent(), [user?.healthGoal]);


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Mobile Dashboard - Only visible on mobile */}
      <div className="md:hidden">
        <MobileDashboard stats={stats} />
      </div>

      {/* Desktop Dashboard - Hidden on mobile */}
      <main className="hidden md:block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-6">

        {/* ─── Desktop Calorie Widget ───────────────────────────────────── */}
        <div className="mb-8">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Bună, {user?.firstName || user?.email?.split('@')[0]}! 👋
              </h1>
              <p className="text-sm text-gray-500 capitalize">{formatDateRo()}</p>
            </div>
          </div>

          <div
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/food-tracking')}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dashboard Calorii</h3>

            {calorieLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              <div className="flex items-center gap-8">
                {/* Circular Progress */}
                <div className="w-28 h-28 flex-shrink-0">
                  <CircularProgressbar
                    value={caloriePercentage}
                    maxValue={100}
                    styles={buildStyles({
                      pathColor: isOverGoal ? '#ef4444' : '#8b5cf6',
                      trailColor: '#f3f4f6',
                      strokeLinecap: 'round',
                    })}
                  />
                </div>

                {/* Calorie Info */}
                <div className="flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-gray-900">
                      {calorieRemaining > 0 ? calorieRemaining : 0}
                    </span>
                    <span className="text-base text-gray-500">kcal rămase</span>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">/ Obiectiv {calorieGoal}</p>

                  <div className="flex items-center gap-8 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span className="text-gray-600">Consumat</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Flame className="w-4 h-4 text-purple-500" />
                      <span className="text-gray-600">Activități</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-8 text-sm mt-1">
                    <div className="flex items-center gap-2">
                      <Utensils className="w-4 h-4 text-amber-500" />
                      <span className="font-semibold text-gray-900">{calorieConsumed} kcal</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-purple-400" />
                      <span className="font-semibold text-gray-900">— kcal</span>
                    </div>
                  </div>
                </div>

                {/* Macro Bars (desktop side-by-side) */}
                <div className="flex gap-4 w-64">
                  <MacroBar label="Proteine" consumed={proteinConsumed} goal={proteinGoal} color="bg-blue-500" />
                  <MacroBar label="Carbohidrați" consumed={carbsConsumed} goal={carbsGoal} color="bg-yellow-500" />
                  <MacroBar label="Grăsimi" consumed={fatConsumed} goal={fatGoal} color="bg-orange-500" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Personalized Tips Banner */}
        {user?.healthGoal && personalizedContent && (
          <div className="mb-8 bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center mb-4">
              <Target className="w-6 h-6 mr-2" />
              <h2 className="text-2xl font-bold">Plan personalizat pentru obiectivul tău</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-3 text-lg">💡 Tips zilnice:</h3>
                <div className="space-y-2">
                  {personalizedContent.tips.map((tip: string, idx: number) => (
                    <div key={idx} className="flex items-start bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                      <span className="text-sm leading-relaxed">{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-3 text-lg">🎯 Focus pe:</h3>
                <div className="space-y-2">
                  {personalizedContent.focusAreas.map((area: string, idx: number) => (
                    <div key={idx} className="flex items-center bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                      <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                      <span className="text-sm font-medium">{area}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigate('/health-goal')}
                  className="mt-4 w-full bg-white text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-purple-50 transition-colors"
                >
                  Schimbă obiectivul
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Modules Grid - Cu badges pentru valori */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {modules.map((module, index) => (
            <Card key={index} interactive onClick={() => navigate(module.path)}>
              <div className="relative">
                {/* VALUE BADGE - Top Right */}
                {module.showBadge && (
                  <div className={`absolute -top-2 -right-2 ${module.badgeColor} text-white px-3 py-1.5 rounded-full font-bold text-lg shadow-lg z-10 border-2 border-white`}>
                    {module.badgeValue}
                  </div>
                )}
                
                <CardHeader
                  icon={module.icon}
                  iconBgColor={module.iconBg}
                  iconColor={module.iconColor}
                  title={module.title}
                  subtitle={module.subtitle}
                />
              </div>
              <CardBody>{module.description}</CardBody>
              <CardFooter>
                <span className={`flex items-center ${module.footerColor}`}>
                  <module.footerIcon className="w-4 h-4 mr-1" />
                  {module.footer}
                </span>
                <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Community Posts Section */}
        {communityPosts.length > 0 && (
          <div className="mb-8">
            <Card>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-xl">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Comunitate - Postări Recente</h3>
                </div>
                <button 
                  onClick={() => navigate('/community')}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                >
                  Vezi tot
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-4">
                {communityPosts.map((post) => (
                  <div
                    key={post.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/community/posts/${post.id}`);
                    }}
                    className="p-4 border-2 border-gray-100 rounded-xl hover:border-purple-200 hover:bg-purple-50/30 transition-all cursor-pointer group"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                          {post.isAnonymous ? '?' : (post.user.firstName?.[0] || 'U')}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {post.isAnonymous ? 'Anonim' : `${post.user.firstName} ${post.user.lastName}`}
                          </p>
                          <p className="text-xs text-gray-500">
                            {getTimeAgo(new Date(post.createdAt))}
                            {post.disease && ` • ${post.disease.name}`}
                          </p>
                        </div>
                      </div>
                      <span className={`
                        px-2 py-1 rounded-full text-xs font-medium
                        ${post.type === 'QUESTION' ? 'bg-blue-100 text-blue-700' : ''}
                        ${post.type === 'EXPERIENCE' ? 'bg-green-100 text-green-700' : ''}
                        ${post.type === 'VICTORY' ? 'bg-yellow-100 text-yellow-700' : ''}
                        ${post.type === 'TIP' ? 'bg-purple-100 text-purple-700' : ''}
                        ${post.type === 'SUPPORT' ? 'bg-pink-100 text-pink-700' : ''}
                        ${post.type === 'GENERAL' ? 'bg-gray-100 text-gray-700' : ''}
                      `}>
                        {post.type === 'QUESTION' && '❓ Întrebare'}
                        {post.type === 'EXPERIENCE' && '💭 Experiență'}
                        {post.type === 'VICTORY' && '🎉 Victorie'}
                        {post.type === 'TIP' && '💡 Sfat'}
                        {post.type === 'SUPPORT' && '🤝 Suport'}
                        {post.type === 'GENERAL' && '💬 General'}
                      </span>
                    </div>

                    {post.title && (
                      <h4 className="text-base font-semibold text-gray-900 mb-2 group-hover:text-purple-700 transition-colors">
                        {post.title}
                      </h4>
                    )}

                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {post.content}
                    </p>

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <ThumbsUp className="w-4 h-4" />
                        <span>{post.likesCount || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MessageCircleHeart className="w-4 h-4" />
                        <span>{post.commentsCount || 0} comentarii</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Activity className="w-4 h-4" />
                        <span>{post.viewsCount || 0} vizualizări</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate('/community')}
                className="mt-6 w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center gap-2"
              >
                <Users className="w-5 h-5" />
                Explorează Comunitatea
              </button>
            </Card>
          </div>
        )}

        {/* Food Community Banner */}
        <div className="mb-8">
          <div
            className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-6 text-white shadow-xl cursor-pointer hover:shadow-2xl transition-shadow"
            onClick={() => navigate('/food-community')}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <Flame className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold">Retete Comunitate</h3>
                </div>
                <p className="text-white/90 text-sm mb-4 max-w-lg">
                  Descopera retete postate de comunitate, obtine analiza nutritionala automata cu AI si salveaza-ti favoritele in colectii personale.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Feed retete', 'Trending', 'Pentru tine', 'Colectiile mele'].map((tag) => (
                    <span key={tag} className="bg-white/20 text-white text-xs font-medium px-3 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="hidden md:flex items-center ml-6">
                <div className="bg-white/20 rounded-2xl p-4 text-center min-w-[100px]">
                  <ChefHat className="w-10 h-10 text-white mx-auto mb-1" />
                  <p className="text-xs text-white/80 font-medium">Posteaza reteta</p>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-white/80 text-sm font-medium">
              <span>Exploreaza acum</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Activitate Recentă</h3>
              <button 
                onClick={() => navigate(isChronicEnabled() ? '/calendar' : '/advanced-tracking')}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Vezi tot
              </button>
            </div>

            {recentActivity.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Nicio activitate înregistrată încă</p>
                <p className="text-sm">Începe să tracking mese, exerciții sau nutriție</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className={`w-8 h-8 ${activity.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
                      <activity.icon className={`w-4 h-4 ${activity.iconColor}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                      <p className="text-xs text-gray-500">{activity.description}</p>
                      <p className="text-xs text-gray-400 mt-1">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
